import { createContext, type FC, type PropsWithChildren,useContext } from 'react';

import type { NoFlipMode } from '../utils/createNoFlip';

import { type DirectionState,useDirection } from './useDirection';

export interface ContentDirectionState extends DirectionState {
  /**
   * Language the content is actually written in, for the DOM `lang` attribute.
   * `null` when the content follows the UI language and needs no marking.
   */
  lang: string | null;
  /** True when the content language differs from the UI language. */
  differsFromUi: boolean;
}

const ContentLanguageContext = createContext<string | null>(null);

/**
 * Declares the language that *generated* content inside this subtree is written
 * in — as opposed to the UI language, which `useDirection` answers.
 *
 * The two come apart on assessment results. Domain names, descriptions and
 * maturity labels are stored per-language and resolved server-side from
 * `Accept-Language`, so they follow the UI. The narrative the AI produced —
 * executive summary, gap titles, "what is needed", remediation steps — is
 * written once in the language the assessment ran in and stored as a plain
 * string. Switching the app to Arabic cannot retranslate it, so an assessment
 * run in English keeps English findings inside an Arabic page.
 *
 * Wrap the results view in this and pin those specific text nodes with
 * `useContentDirection()`, so English prose is laid out (and announced) as
 * English instead of inheriting the page's RTL.
 */
// Not a mobx `observer`: it reads no observable state, and making every results
// view a reaction boundary would buy nothing. Consumers stay observers.
// eslint-disable-next-line mobx/missing-observer
export const ContentLanguageProvider: FC<PropsWithChildren<{ language: string | null }>> = ({
  language,
  children,
}) => <ContentLanguageContext.Provider value={language}>{children}</ContentLanguageContext.Provider>;

/**
 * Direction and `lang` for generated content in the current subtree.
 *
 * Falls back to the UI direction when no `ContentLanguageProvider` is above it,
 * so it is safe to call from a component that is also rendered outside a
 * results view.
 */
export const useContentDirection = (): ContentDirectionState => {
  const contentLanguage = useContext(ContentLanguageContext);
  const ui = useDirection();

  if (!contentLanguage) {
    return { ...ui, lang: null, differsFromUi: false };
  }

  const normalized = contentLanguage.toLowerCase();
  const isRtl = normalized.startsWith('ar');
  const dir: NoFlipMode = isRtl ? 'rtl' : 'ltr';

  return { dir, isRtl, lang: normalized.split('-')[0], differsFromUi: isRtl !== ui.isRtl };
};
